import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Link } from "@material-ui/core";

import { buildFolioCells } from "./folioMatrixLogic";

const COLOR_ASIGNADO = "#66bb6a";
const COLOR_RESERVADO = "#ffa726";
const COLOR_LIBRE = "#e0e0e0";
const COLOR_FUERA_DE_RANGO = "#ef5350";

function colorDeCelda(numero, asignados, reservados) {
  if (asignados.has(numero)) return COLOR_ASIGNADO;
  if (reservados.has(numero)) return COLOR_RESERVADO;
  return COLOR_LIBRE;
}

export default function FolioMatrixGrid({
  modalidad,
  asignados,
  reservados,
  onAbrirFolio,
  foliosFueraDeRango,
  onAbrirAtleta,
}) {
  const [expandido, setExpandido] = useState(false);

  if (modalidad.desde > modalidad.hasta) {
    return (
      <Box mb={3}>
        <Typography variant="subtitle1">{modalidad.label}</Typography>
        <Typography color="error">
          Rango mal configurado: "desde" ({modalidad.desde}) es mayor que "hasta" (
          {modalidad.hasta}).
        </Typography>
      </Box>
    );
  }

  const cells = buildFolioCells(
    modalidad.desde,
    modalidad.hasta,
    new Set([...asignados, ...reservados]),
    expandido
  );

  return (
    <Box mb={3}>
      <Box display="flex" alignItems="center" style={{ gap: 12 }}>
        <Typography variant="subtitle1" gutterBottom>
          {modalidad.label} ({modalidad.desde}–{modalidad.hasta})
        </Typography>
        {expandido && (
          <Link component="button" variant="body2" onClick={() => setExpandido(false)}>
            Volver a resumir
          </Link>
        )}
      </Box>
      <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
        {cells.map((cell, i) =>
          cell.type === "ellipsis" ? (
            <Box
              key={`ellipsis-${i}`}
              display="flex"
              alignItems="center"
              justifyContent="center"
              onClick={() => setExpandido(true)}
              title="Mostrar todos los folios de este rango"
              style={{ width: 32, height: 32, fontWeight: "bold", cursor: "pointer" }}
            >
              …
            </Box>
          ) : (
            <Box
              key={cell.numero}
              onClick={() => onAbrirFolio(cell.numero)}
              title={cell.numero}
              style={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colorDeCelda(cell.numero, asignados, reservados),
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 10,
                overflow: "hidden",
              }}
            >
              {cell.numero}
            </Box>
          )
        )}
      </Box>

      {foliosFueraDeRango && foliosFueraDeRango.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="error" gutterBottom>
            Folios asignados a esta modalidad fuera de su rango ({modalidad.desde}–
            {modalidad.hasta}) — probablemente por un cambio de rango en la modalidad o una asignación manual:
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
            {foliosFueraDeRango.map((f) => (
              <Box
                key={f.numero}
                onClick={() => onAbrirAtleta(f.inscritoKey)}
                title={`${f.numero} — ${f.nombreAtleta || f.inscritoKey}`}
                style={{
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: COLOR_FUERA_DE_RANGO,
                  color: "#fff",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: 10,
                  overflow: "hidden",
                }}
              >
                {f.numero}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

FolioMatrixGrid.propTypes = {
  modalidad: PropTypes.shape({
    codigo: PropTypes.string,
    label: PropTypes.string,
    desde: PropTypes.number,
    hasta: PropTypes.number,
  }).isRequired,
  asignados: PropTypes.instanceOf(Set).isRequired,
  reservados: PropTypes.instanceOf(Set).isRequired,
  onAbrirFolio: PropTypes.func.isRequired,
  foliosFueraDeRango: PropTypes.arrayOf(
    PropTypes.shape({
      numero: PropTypes.number,
      inscritoKey: PropTypes.string,
      nombreAtleta: PropTypes.string,
    })
  ),
  onAbrirAtleta: PropTypes.func,
};

FolioMatrixGrid.defaultProps = {
  foliosFueraDeRango: [],
  onAbrirAtleta: () => {},
};
